import { Chip } from "@mui/material";
import {
  RadioButtonUnchecked as OpenIcon,
  LocalShipping as DispatchedIcon,
  NotificationsActive as TechAwareIcon,
  LocationOn as OnSiteIcon,
  CheckCircle as CompletedIcon,
  Lock as ClosedIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import type { WorkOrderType, WorkOrderStatus, WorkOrderPriority } from "../types";

// Type badge
interface TypeBadgeProps {
  type: WorkOrderType;
  size?: "small" | "medium";
}

const typeConfig: Record<WorkOrderType, { label: string; color: "error" | "warning" | "info" | "success" | "secondary" | "default" }> = {
  earth_moving: { label: "Earth Moving", color: "error" },
  road_works: { label: "Road Works", color: "warning" },
  building_works: { label: "Building", color: "warning" },
  machinery_parked: { label: "Machinery", color: "info" },
  agi_activity: { label: "AGI", color: "secondary" },
  marker_posts: { label: "Marker Post", color: "default" },
  forestry: { label: "Forestry", color: "success" },
  quarry: { label: "Quarry", color: "error" },
  flooding: { label: "Flooding", color: "info" },
  ditching: { label: "Ditching", color: "info" },
  network_works: { label: "Network", color: "success" },
  fencing: { label: "Fencing", color: "default" },
  other: { label: "Other", color: "default" },
  unidentified: { label: "Unidentified", color: "warning" },
};

export const WorkOrderTypeBadge = ({ type, size = "small" }: TypeBadgeProps) => {
  const config = typeConfig[type];
  return <Chip label={config.label} size={size} color={config.color} variant="outlined" />;
};

// Status badge
interface StatusBadgeProps {
  status: WorkOrderStatus;
  size?: "small" | "medium";
}

const statusConfig: Record<WorkOrderStatus, { label: string; color: "error" | "warning" | "info" | "success" | "default"; icon: React.ReactElement }> = {
  open: { label: "Open", color: "error", icon: <OpenIcon /> },
  dispatched: { label: "Dispatched", color: "warning", icon: <DispatchedIcon /> },
  tech_aware: { label: "Tech Aware", color: "info", icon: <TechAwareIcon /> },
  on_site: { label: "On Site", color: "info", icon: <OnSiteIcon /> },
  completed: { label: "Completed", color: "success", icon: <CompletedIcon /> },
  closed: { label: "Closed", color: "default", icon: <ClosedIcon /> },
};

export const WorkOrderStatusBadge = ({ status, size = "small" }: StatusBadgeProps) => {
  const config = statusConfig[status];
  return <Chip icon={config.icon} label={config.label} size={size} color={config.color} variant="outlined" />;
};

// Priority badge
interface PriorityBadgeProps {
  priority: WorkOrderPriority;
  size?: "small" | "medium";
}

const priorityConfig: Record<WorkOrderPriority, { label: string; color: "error" | "warning" | "info" | "success" }> = {
  critical: { label: "Critical", color: "error" },
  high: { label: "High", color: "warning" },
  medium: { label: "Medium", color: "info" },
  low: { label: "Low", color: "success" },
};

export const WorkOrderPriorityBadge = ({ priority, size = "small" }: PriorityBadgeProps) => {
  const config = priorityConfig[priority];
  return <Chip label={config.label} size={size} color={config.color} />;
};

// SLA badge
interface SLABadgeProps {
  breached: boolean;
  size?: "small" | "medium";
}

export const SLABadge = ({ breached, size = "small" }: SLABadgeProps) => {
  return breached ? (
    <Chip icon={<WarningIcon />} label="SLA Breached" size={size} color="error" />
  ) : null;
};
